/**
 * Airport facts for a quote whose vendor gives no schedule: the flight time
 * guessed from great-circle distance at cruise speed plus a fixed buffer, and
 * the zone a connection's offset-less stamp is written in. The airports are
 * the major cargo airports (airports.ts) plus common US hubs, copied from the
 * backend's geo/airports.json; an airport outside it has neither.
 */
const CRUISE_KMH = 800;
const BUFFER_MINUTES = 45;
const EARTH_RADIUS_KM = 6371;

const AIRPORTS: Record<string, [lat: number, lng: number, zone: string]> = {
  PHL: [39.8719, -75.2411, 'America/New_York'],
  JFK: [40.6394, -73.7793, 'America/New_York'],
  EWR: [40.6894, -74.1705, 'America/New_York'],
  BOS: [42.362, -71.0079, 'America/New_York'],
  ORD: [41.9786, -87.9048, 'America/Chicago'],
  ATL: [33.6367, -84.4281, 'America/New_York'],
  MIA: [25.796, -80.2898, 'America/New_York'],
  DFW: [32.8968, -97.038, 'America/Chicago'],
  IAH: [29.9844, -95.3414, 'America/Chicago'],
  LAX: [33.9425, -118.408, 'America/Los_Angeles'],
  SFO: [37.6198, -122.3748, 'America/Los_Angeles'],
  SEA: [47.4479, -122.3103, 'America/Los_Angeles'],
  MEM: [35.0438, -89.9763, 'America/Chicago'],
  SDF: [38.1706, -85.7351, 'America/Kentucky/Louisville'],
  CVG: [39.0488, -84.6678, 'America/New_York'],
  IND: [39.7173, -86.2944, 'America/Indiana/Indianapolis'],
  RFD: [42.1954, -89.0972, 'America/Chicago'],
  BWI: [39.1754, -76.6683, 'America/New_York'],
  IAD: [38.9445, -77.4558, 'America/New_York'],
  DEN: [39.86, -104.6738, 'America/Denver'],
  PHX: [33.4353, -112.0059, 'America/Phoenix'],
  MSP: [44.8801, -93.2217, 'America/Chicago'],
  DTW: [42.2138, -83.3538, 'America/Detroit'],
  RDU: [35.8787, -78.7873, 'America/New_York'],
  SJU: [18.4394, -66.0018, 'America/Puerto_Rico'],
  YYZ: [43.6759, -79.6294, 'America/Toronto'],
  YUL: [45.4678, -73.7423, 'America/Toronto'],
  YVR: [49.1939, -123.184, 'America/Vancouver'],
  MEX: [19.4358, -99.0703, 'America/Mexico_City'],
  GDL: [20.5233, -103.3101, 'America/Mexico_City'],
  FRA: [50.0267, 8.5584, 'Europe/Berlin'],
  LEJ: [51.4207, 12.2327, 'Europe/Berlin'],
  MUC: [48.3538, 11.7861, 'Europe/Berlin'],
  CGN: [50.8659, 7.1427, 'Europe/Berlin'],
  HAM: [53.6304, 9.9882, 'Europe/Berlin'],
  AMS: [52.3086, 4.7639, 'Europe/Amsterdam'],
  LHR: [51.4707, -0.4599, 'Europe/London'],
  STN: [51.885, 0.235, 'Europe/London'],
  EMA: [52.8311, -1.3281, 'Europe/London'],
  CDG: [49.009, 2.5541, 'Europe/Paris'],
  LGG: [50.6386, 5.4439, 'Europe/Brussels'],
  BRU: [50.9014, 4.4844, 'Europe/Brussels'],
  LUX: [49.6268, 6.2121, 'Europe/Luxembourg'],
  ZRH: [47.4581, 8.5481, 'Europe/Zurich'],
  BSL: [47.6007, 7.5211, 'Europe/Paris'],
  GVA: [46.2381, 6.109, 'Europe/Zurich'],
  VIE: [48.1103, 16.5697, 'Europe/Vienna'],
  MXP: [45.6306, 8.7281, 'Europe/Rome'],
  FCO: [41.8045, 12.252, 'Europe/Rome'],
  MAD: [40.4934, -3.5722, 'Europe/Madrid'],
  BCN: [41.2971, 2.0785, 'Europe/Madrid'],
  LIS: [38.7813, -9.1359, 'Europe/Lisbon'],
  DUB: [53.4287, -6.2621, 'Europe/Dublin'],
  CPH: [55.6179, 12.656, 'Europe/Copenhagen'],
  ARN: [59.6485, 17.9288, 'Europe/Stockholm'],
  OSL: [60.1939, 11.1004, 'Europe/Oslo'],
  HEL: [60.3184, 24.9633, 'Europe/Helsinki'],
  WAW: [52.1657, 20.9671, 'Europe/Warsaw'],
  PRG: [50.1009, 14.2599, 'Europe/Prague'],
  BUD: [47.4302, 19.2624, 'Europe/Budapest'],
  OTP: [44.5718, 26.1033, 'Europe/Bucharest'],
  IST: [41.2749, 28.7321, 'Europe/Istanbul'],
  ATH: [37.9364, 23.9445, 'Europe/Athens'],
  DXB: [25.2498, 55.371, 'Asia/Dubai'],
  DWC: [24.8962, 55.1624, 'Asia/Dubai'],
  AUH: [24.441, 54.6492, 'Asia/Dubai'],
  DOH: [25.2731, 51.6081, 'Asia/Qatar'],
  RUH: [24.9576, 46.6988, 'Asia/Riyadh'],
  JED: [21.6802, 39.1574, 'Asia/Riyadh'],
  TLV: [32.0114, 34.8867, 'Asia/Jerusalem'],
  HKG: [22.3118, 113.9149, 'Asia/Hong_Kong'],
  PVG: [31.1434, 121.805, 'Asia/Shanghai'],
  PEK: [40.0773, 116.5967, 'Asia/Shanghai'],
  CAN: [23.3924, 113.299, 'Asia/Shanghai'],
  SZX: [22.6395, 113.8033, 'Asia/Shanghai'],
  TPE: [25.0777, 121.233, 'Asia/Taipei'],
  NRT: [35.7686, 140.3887, 'Asia/Tokyo'],
  KIX: [34.4273, 135.244, 'Asia/Tokyo'],
  ICN: [37.4691, 126.451, 'Asia/Seoul'],
  SIN: [1.3502, 103.994, 'Asia/Singapore'],
  BKK: [13.6811, 100.747, 'Asia/Bangkok'],
  KUL: [2.7456, 101.71, 'Asia/Kuala_Lumpur'],
  CGK: [-6.1256, 106.656, 'Asia/Jakarta'],
  MNL: [14.5086, 121.02, 'Asia/Manila'],
  SGN: [10.8188, 106.652, 'Asia/Ho_Chi_Minh'],
  HAN: [21.2212, 105.807, 'Asia/Bangkok'],
  DEL: [28.5556, 77.0952, 'Asia/Kolkata'],
  BOM: [19.0887, 72.8679, 'Asia/Kolkata'],
  MAA: [12.99, 80.1693, 'Asia/Kolkata'],
  BLR: [13.1979, 77.7063, 'Asia/Kolkata'],
  HYD: [17.2313, 78.4299, 'Asia/Kolkata'],
  ALA: [43.3543, 77.0428, 'Asia/Almaty'],
  GYD: [40.4728, 50.0509, 'Asia/Baku'],
  SYD: [-33.9461, 151.177, 'Australia/Sydney'],
  MEL: [-37.6707, 144.8379, 'Australia/Melbourne'],
  AKL: [-37.012, 174.7863, 'Pacific/Auckland'],
  GRU: [-23.4313, -46.47, 'America/Sao_Paulo'],
  VCP: [-23.0074, -47.1345, 'America/Sao_Paulo'],
  EZE: [-34.8222, -58.5358, 'America/Argentina/Buenos_Aires'],
  SCL: [-33.393, -70.7858, 'America/Santiago'],
  BOG: [4.7016, -74.1469, 'America/Bogota'],
  UIO: [-0.1254, -78.3543, 'America/Guayaquil'],
  LIM: [-12.0219, -77.1143, 'America/Lima'],
  JNB: [-26.1401, 28.2468, 'Africa/Johannesburg'],
  CPT: [-33.974, 18.6043, 'Africa/Johannesburg'],
  NBO: [-1.3189, 36.9282, 'Africa/Nairobi'],
  ADD: [8.9779, 38.7993, 'Africa/Addis_Ababa'],
  CAI: [30.1115, 31.3967, 'Africa/Cairo'],
  CMN: [33.3675, -7.59, 'Africa/Casablanca'],
  LOS: [6.5774, 3.3212, 'Africa/Lagos'],
  SJC: [37.3625, -121.9292, 'America/Los_Angeles'],
  CLT: [35.214, -80.9431, 'America/New_York'],
  LAS: [36.0834, -115.1518, 'America/Los_Angeles'],
  SLC: [40.7889, -111.9799, 'America/Denver'],
  PDX: [45.5887, -122.598, 'America/Los_Angeles'],
  SAN: [32.7336, -117.19, 'America/Los_Angeles'],
  OAK: [37.7201, -122.2212, 'America/Los_Angeles'],
  ONT: [34.056, -117.601, 'America/Los_Angeles'],
  ABQ: [35.04, -106.6089, 'America/Denver'],
  AUS: [30.1975, -97.662, 'America/Chicago'],
  BNA: [36.1245, -86.6782, 'America/Chicago'],
  CLE: [41.4117, -81.8498, 'America/New_York'],
  CMH: [39.998, -82.8919, 'America/New_York'],
  DAL: [32.8448, -96.8477, 'America/Chicago'],
  MCI: [39.3017, -94.7139, 'America/Chicago'],
  MKE: [42.9472, -87.8966, 'America/Chicago'],
  MSY: [29.9934, -90.2647, 'America/Chicago'],
  OMA: [41.3032, -95.8941, 'America/Chicago'],
  PIT: [40.4915, -80.2329, 'America/New_York'],
  SAT: [29.5337, -98.4698, 'America/Chicago'],
  STL: [38.7487, -90.37, 'America/Chicago'],
  TPA: [27.9755, -82.5332, 'America/New_York'],
  TUS: [32.115, -110.9381, 'America/Phoenix'],
};

const airportOf = (code?: string | null) =>
  AIRPORTS[code?.trim().toUpperCase() ?? ''];

export const airportZoneOf = (code?: string | null): string | null =>
  airportOf(code)?.[2] ?? null;

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** Estimated gate-to-gate minutes between two airport codes; null when either is unknown or they are the same. */
export const estimateFlightMinutes = (
  origin?: string | null,
  destination?: string | null
): number | null => {
  const from = airportOf(origin);
  const to = airportOf(destination);
  if (!from || !to) return null;
  const [lat1, lng1] = [from[0], from[1]].map(toRadians);
  const [lat2, lng2] = [to[0], to[1]].map(toRadians);
  const a =
    Math.sin((lat2 - lat1) / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin((lng2 - lng1) / 2) ** 2;
  const km = 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
  if (km === 0) return null;
  // rounded to 5 minutes: the figure is a guess, not a schedule
  return Math.round((km / CRUISE_KMH) * 12 + BUFFER_MINUTES / 5) * 5;
};
